/**
 * Built-in attacking-corner templates.
 *
 * Ready-made corner routines a coach can import into their set-piece
 * library (`setPiecesStore`) and tweak on the board. Each template is
 * the shape `saveSetPiece` accepts — `TacticsSetPiece` minus the
 * store-minted `id` + `createdAt` — so importing is a plain save of a
 * copy.
 *
 * IMPORTANT — no position swapping. The board binds player identity
 * (jersey number + roster name) to the *index* of a spot in
 * `homeSpots` / `awaySpots`. So every template lists its 11 spots in
 * conventional 1–11 order — GK, back four, midfield, forwards — and
 * keeps conventional role labels (never abstract "CK" / "NP" tags).
 * Applying a template therefore moves player #4 to a centre-back-ish
 * corner position, not to "wherever slot 4 happens to be" — each
 * player keeps both their number and a sensible job.
 *
 * All templates are LEFT-side attacking corners into the away end
 * (−Z). Coordinates are world metres, pitch-centred.
 */

import { originForCorner } from './setPieces';
import type { SetPieceSpot, TacticsArrow, TacticsSetPiece } from './setPieces';
import { FIELD_LENGTH_M } from './adapters';

/** A corner template — exactly what `saveSetPiece` takes. */
export type SetPieceTemplate = Omit<TacticsSetPiece, 'id' | 'createdAt'>;

const HALF_L = FIELD_LENGTH_M / 2; // 52.5
const ORIGIN = originForCorner('left', 'away'); // { x: -34, z: -52.5 }
const GOAL = -HALF_L; // away goal line
/** Corner taker's spot — just inside the flag. Index 6 in every home
 *  template (a central midfielder takes it). */
const TAKER = { x: ORIGIN.x + 1, z: ORIGIN.z + 1 };

/**
 * GK + back four — conventional indices 0–4. They hold for the corner
 * (transition cover), identical across templates. The two full-backs
 * sit a touch higher than the centre-backs.
 */
function backLine(): SetPieceSpot[] {
  return [
    { role: 'GK', x: 0, z: HALF_L - 4 }, // 1 — own half, deep
    { role: 'RB', x: 18, z: 15 }, // 2 — transition cover, right
    { role: 'LB', x: -18, z: 15 }, // 3 — transition cover, left
    { role: 'CB', x: -7, z: 8 }, // 4 — transition cover, central
    { role: 'CB', x: 7, z: 8 }, // 5 — transition cover, central
  ];
}

/**
 * Away side — defending the corner, conventional 1–11 order, packed
 * zonally into the box. Shared by every template.
 */
function boxDefence(): SetPieceSpot[] {
  return [
    { role: 'GK', x: 0, z: GOAL + 1 }, // 1
    { role: 'RB', x: 3.6, z: GOAL + 1.5 }, // 2 — far post
    { role: 'LB', x: -3.6, z: GOAL + 1.5 }, // 3 — near post
    { role: 'CB', x: -7, z: GOAL + 5 }, // 4
    { role: 'CB', x: 7, z: GOAL + 6 }, // 5
    { role: 'CM', x: 0, z: GOAL + 5.5 }, // 6
    { role: 'CM', x: -10, z: GOAL + 11 }, // 7
    { role: 'CM', x: 4, z: GOAL + 11 }, // 8
    { role: 'LW', x: -23, z: GOAL + 9 }, // 9 — marks the short option
    { role: 'ST', x: -2, z: GOAL + 16 }, // 10 — edge of box
    { role: 'RW', x: 0, z: GOAL + 26 }, // 11 — counter outlet
  ];
}

function run(id: string, from: { x: number; z: number }, to: { x: number; z: number }): TacticsArrow {
  return { id, points: [from, to], color: '#4ade80' };
}

/**
 * The templates. Home side is always conventional 1–11:
 * [GK, RB, LB, CB, CB, CM, CM(taker), CM, LW, ST, RW]. The midfield +
 * forwards carry the routine; index 6 always takes the corner.
 */
export const CORNER_TEMPLATES: SetPieceTemplate[] = [
  {
    name: 'Near-post flick-on',
    type: 'corner-att',
    origin: ORIGIN,
    delivery: 'in-swinger',
    homeSpots: [
      ...backLine(),
      { role: 'CM', x: 0, z: GOAL + 16 }, // 6 — edge of box, second ball
      { role: 'CM', x: TAKER.x, z: TAKER.z }, // 7 — takes the corner
      { role: 'CM', x: -22, z: GOAL + 10 }, // 8 — short-corner decoy
      { role: 'LW', x: -5, z: GOAL + 4.5 }, // 9 — near-post flick run
      { role: 'ST', x: 0, z: GOAL + 6.5 }, // 10 — central, second ball
      { role: 'RW', x: 5, z: GOAL + 3.5 }, // 11 — far post, gambles
    ],
    awaySpots: boxDefence(),
    arrows: [
      run('npt-near', { x: -5, z: GOAL + 9 }, { x: -5, z: GOAL + 4.5 }),
      run('npt-far', { x: 5, z: GOAL + 9 }, { x: 5, z: GOAL + 3.5 }),
    ],
  },
  {
    name: 'Back-post overload',
    type: 'corner-att',
    origin: ORIGIN,
    delivery: 'out-swinger',
    homeSpots: [
      ...backLine(),
      { role: 'CM', x: 2, z: GOAL + 16 }, // 6 — edge of box
      { role: 'CM', x: TAKER.x, z: TAKER.z }, // 7 — takes the corner
      { role: 'CM', x: 14, z: GOAL + 10 }, // 8 — right side, second ball
      { role: 'LW', x: -6, z: GOAL + 5 }, // 9 — near-post decoy
      { role: 'ST', x: 6, z: GOAL + 3.5 }, // 10 — back post
      { role: 'RW', x: 10, z: GOAL + 5.5 }, // 11 — back-post overload
    ],
    awaySpots: boxDefence(),
    arrows: [
      run('bpt-st', { x: 6, z: GOAL + 11 }, { x: 6, z: GOAL + 3.5 }),
      run('bpt-rw', { x: 10, z: GOAL + 12 }, { x: 10, z: GOAL + 5.5 }),
    ],
  },
  {
    name: 'Short-corner combination',
    type: 'corner-att',
    origin: ORIGIN,
    delivery: 'short',
    homeSpots: [
      ...backLine(),
      { role: 'CM', x: -3, z: GOAL + 16 }, // 6 — edge of box
      { role: 'CM', x: TAKER.x, z: TAKER.z }, // 7 — takes the corner
      { role: 'CM', x: -25, z: GOAL + 7.5 }, // 8 — receives the short pass
      { role: 'LW', x: -19, z: GOAL + 13 }, // 9 — overlap support
      { role: 'ST', x: 0, z: GOAL + 5.5 }, // 10 — box target
      { role: 'RW', x: 6, z: GOAL + 4.5 }, // 11 — box target
    ],
    awaySpots: boxDefence(),
    arrows: [
      run('sct-pass', TAKER, { x: -25, z: GOAL + 7.5 }),
      run('sct-overlap', { x: -19, z: GOAL + 13 }, { x: -21, z: GOAL + 6 }),
    ],
  },
];
